import { useState, useEffect, type ReactNode } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, GitCompare, Building2, ShieldAlert, FileSpreadsheet, Users, LogOut, TrendingDown, LineChart, Percent, Layers, Activity, Globe2, Menu, X, ShieldCheck, Upload, FileText, Shield, AlertTriangle, ClipboardList, Phone, Wallet, Zap, Droplet, Kanban, GitMerge, Star, Recycle, Radar, Landmark, ChevronRight, PanelLeft, PanelLeftClose, ArrowLeftRight, Users2, Beef, Calculator, FolderOpen, Database, Search, Fingerprint, Workflow, Newspaper, Gauge, Code2, RefreshCw, KeyRound, Eye, Factory, Leaf, Wheat } from "lucide-react";
import { FidcSelector } from "@/components/ui/fidc-selector";
import { CommandPalette } from "./CommandPalette";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { logout, apiFetch, type AuthUser } from "@/lib/api";
import { useSession } from "@/lib/session";
import { usePermissions } from "@/lib/permissions";

type ProductKey = "consignado_clt" | "agro";
type NavItem = { to: string; label: string; icon: typeof LayoutDashboard; roles?: AuthUser["role"][]; product?: ProductKey };
type NavGroup = { label: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Visão",
    items: [
      { to: "/app", label: "Cockpit", icon: LayoutDashboard },
      { to: "/app/dashboard-classic", label: "Dashboard clássico", icon: LayoutDashboard },
    ],
  },
  {
    label: "Análise de risco",
    items: [
      { to: "/app/losses", label: "Perdas e atraso", icon: TrendingDown, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/aging", label: "Atraso 30/60/90/120d", icon: AlertTriangle, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/enquadramento", label: "Enquadramento", icon: ShieldCheck, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/ifrs9", label: "Provisão de risco (IFRS9)", icon: Shield, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/pdd", label: "PDD e razões", icon: ShieldCheck, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/vintage", label: "Safragem", icon: LineChart, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/payment-schedule", label: "Cronograma PMTs", icon: LineChart, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/simulations", label: "Simulações", icon: Activity, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/regional-risk", label: "Risco regional", icon: Globe2, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/macro-stats", label: "Falência + Emprego", icon: TrendingDown, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/sensibilidade-macro", label: "Sensibilidade macro setorial", icon: Gauge, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/stress-test", label: "Stress test", icon: Zap, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/roll-rate", label: "Roll & cure rate", icon: GitMerge, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/risco-municipal", label: "Risco município & atividade", icon: Landmark, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "Carteira",
    items: [
      { to: "/app/rate", label: "Taxa efetiva", icon: Percent, roles: ["admin", "gestor_fundo", "consultor"], product: "consignado_clt" },
      { to: "/app/cotas", label: "Cotas Sr/Mez/Jr", icon: Layers, roles: ["admin", "gestor_fundo", "consultor"], product: "consignado_clt" },
      { to: "/app/admin/fidc-tranches", label: "Cotas FIDC", icon: Layers, roles: ["admin"] },
      { to: "/app/distribuicoes", label: "Distribuições", icon: Wallet, roles: ["admin", "gestor_fundo", "consultor"], product: "consignado_clt" },
      { to: "/app/subordinacao-liquidez", label: "Subordinação & liquidez", icon: Droplet, roles: ["admin", "gestor_fundo", "consultor"], product: "consignado_clt" },
      { to: "/app/cnpj", label: "CNPJ devedores", icon: Building2, product: "consignado_clt" },
      { to: "/app/fingerprint", label: "Fingerprint", icon: ShieldAlert, roles: ["admin", "gestor_fundo", "consultor"], product: "consignado_clt" },
    ],
  },
  {
    label: "Avaliação",
    items: [
      { to: "/app/loan-tape", label: "Loan tape", icon: ClipboardList, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "consignado_clt" },
      { to: "/app/risco-carteira", label: "Risco da carteira", icon: TrendingDown, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/peer-benchmark", label: "Peer benchmarking", icon: Radar, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "Estruturação",
    items: [
      { to: "/app/estruturacao-fidc", label: "Estruturação FIDC", icon: Calculator, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "FIAGRO",
    items: [
      { to: "/app/fiagro", label: "FIAGRO", icon: Beef, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/cpr-fluxo", label: "Fluxo das CPRs", icon: Workflow, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/confinamentos", label: "Confinamentos", icon: Beef, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/plantas-sif", label: "Plantas & Offtakers", icon: Factory, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/socioambiental", label: "Risco Socioambiental", icon: Leaf, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/insumos", label: "Insumos & Custo", icon: Wheat, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/cpr-mercado", label: "CPRs de Mercado", icon: LineChart, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "agro" },
      { to: "/app/fiagro/vert-buckets", label: "Buckets VERT (S3)", icon: FolderOpen, roles: ["admin"] },
      { to: "/app/fiagro/vert-fundo", label: "Dados do Fundo VERT", icon: Database, roles: ["admin"] },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/app/kyc", label: "KYC / PLD trail", icon: ShieldCheck, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "Operacional",
    items: [
      { to: "/app/agente-cobranca", label: "Agente de cobrança", icon: Phone, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "consignado_clt" },
      { to: "/app/concessoes", label: "Concessões (pipeline)", icon: Kanban, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "consignado_clt" },
      { to: "/app/originadoras", label: "Originadoras (ranking)", icon: Star, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "consignado_clt" },
      { to: "/app/recompra", label: "Recompra & coobrigação", icon: Recycle, roles: ["admin", "gestor_fundo", "consultor", "originador"], product: "consignado_clt" },
      { to: "/app/conciliation", label: "Conciliação", icon: GitCompare, product: "consignado_clt" },
      { to: "/app/recon-matching", label: "Conciliação aproximada", icon: GitCompare, roles: ["admin", "gestor_fundo", "consultor"], product: "consignado_clt" },
      { to: "/app/dataprev", label: "Dataprev × repasses", icon: FileSpreadsheet, roles: ["admin", "gestor_fundo", "originador"], product: "consignado_clt" },
      { to: "/app/imports", label: "Importar CSV", icon: Upload, roles: ["admin", "consultor"] },
      { to: "/app/reports", label: "Relatórios", icon: FileText },
      { to: "/app/audit-internal", label: "Auditoria Interna", icon: FileText, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "Base Empresas",
    items: [
      { to: "/app/empresas/busca", label: "Busca Empresas", icon: Search, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/empresas/fingerprint", label: "Fingerprint Empresas", icon: Fingerprint, roles: ["admin", "gestor_fundo", "consultor"] },
      { to: "/app/empresas/divida-ativa", label: "Dívida Ativa", icon: Landmark, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "Dados de Mercado",
    items: [
      { to: "/app/admin/pesquisa-cvm", label: "Pesquisa CVM", icon: Landmark, roles: ["admin"] },
      { to: "/app/admin/material-fidc", label: "Material de FIDC", icon: FileText, roles: ["admin"] },
      { to: "/app/dados-transacoes", label: "Dados de Transações", icon: ArrowLeftRight, roles: ["admin"] },
      { to: "/app/admin/relatorios-rating", label: "Relatórios de Rating", icon: ShieldCheck, roles: ["admin"] },
      { to: "/app/admin/relatorios-risco", label: "Relatórios de Risco", icon: ShieldAlert, roles: ["admin"] },
      { to: "/app/admin/inadimplencia-matriz", label: "Matriz de Risco (Inadimplência)", icon: AlertTriangle, roles: ["admin"] },
      { to: "/app/consignado-privado", label: "Consignado Privado (BACEN)", icon: Wallet, roles: ["admin"] },
      { to: "/app/admin/informacoes-mercado", label: "Companhias Abertas", icon: Building2, roles: ["admin"] },
      { to: "/app/analise-gestoras", label: "Análise de Gestoras", icon: Users2, roles: ["admin"] },
      { to: "/app/analise-administrador", label: "Análise de Administrador", icon: Building2, roles: ["admin"] },
      { to: "/app/admin/interest-rate-forecast", label: "Curvas CDI/SELIC/IPCA", icon: LineChart, roles: ["admin"] },
      { to: "/app/indices-setoriais", label: "Índices Setoriais", icon: LineChart, roles: ["admin"] },
    ],
  },
  {
    label: "Dívida Ativa FGTS",
    items: [
      { to: "/app/empresas/fgts", label: "Deterioração FGTS", icon: Landmark, roles: ["admin", "gestor_fundo", "consultor"] },
    ],
  },
  {
    label: "Editoriais de Risco",
    items: [
      { to: "/app/admin/editoriais-risco", label: "Risco Setorial", icon: Newspaper, roles: ["admin"] },
    ],
  },
  {
    label: "Desenvolvedor",
    items: [
      { to: "/app/dev/api-docs", label: "Documentação de APIs", icon: Code2, roles: ["admin", "gestor_fundo", "consultor", "originador"] },
      { to: "/app/dev/tokens", label: "Tokens de API", icon: KeyRound, roles: ["admin"] },
      { to: "/app/dev/troca-arquivos", label: "Troca de arquivos", icon: RefreshCw, roles: ["admin", "gestor_fundo", "consultor", "originador"] },
    ],
  },
  {
    label: "Alocação / Investidor",
    items: [
      { to: "/app/admin/investor-grants", label: "FIDCs por investidor", icon: Landmark, roles: ["admin"] },
      { to: "/investidor/posicoes", label: "Visualização", icon: Eye, roles: ["admin"] },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/app/admin/users", label: "Usuários", icon: Users, roles: ["admin"] },
      { to: "/app/admin/companies", label: "Empresas", icon: Building2, roles: ["admin"] },
      { to: "/app/admin/governance", label: "Governança de acesso", icon: ShieldCheck, roles: ["admin"] },
      { to: "/app/admin/audit", label: "Auditoria", icon: Shield, roles: ["admin"] },
      { to: "/app/admin/automation", label: "Automação", icon: Activity, roles: ["admin"] },
      { to: "/app/admin/system-health", label: "Saúde do sistema", icon: Activity, roles: ["admin"] },
      { to: "/app/admin/trajectory-quality", label: "Qualidade trajetória", icon: ShieldAlert, roles: ["admin"] },
    ],
  },
];

const ROLE_LABEL: Record<AuthUser["role"], string> = {
  admin: "Admin CredCapital",
  gestor_fundo: "Gestor do Fundo",
  consultor: "Consultor",
  originador: "Originador",
  cotista: "Cotista",
};

const NAV_OPEN_KEY = "vsc_nav_groups";

function NavList({ user, products, isAdmin, onNavigate }: { user: AuthUser; products: string[] | null; isAdmin: boolean; onNavigate?: () => void }) {
  const location = useLocation();
  const { isFeatureAllowed } = usePermissions();
  const groups = NAV_GROUPS
    .map((g) => ({
      label: g.label,
      visible: g.items.filter((n) =>
        (!n.roles || n.roles.includes(user.role)) &&
        (isAdmin || !n.product || (products?.includes(n.product) ?? false)) &&
        // eixo tenant/perfil/usuário: entitlement + restrição de perfil + override + kill-switch (feature = rota)
        isFeatureAllowed(n.to),
      ),
    }))
    .filter((g) => g.visible.length > 0);

  // grupo que contém a rota ativa (auto-expande)
  const isItemActive = (to: string) => to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(to);
  const activeGroup = groups.find((g) => g.visible.some((it) => isItemActive(it.to)))?.label ?? null;

  // estado de expansão: começa retraído; grupo ativo aberto; toggles manuais persistem
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(NAV_OPEN_KEY) || "{}"); } catch { return {}; }
  });
  const isOpen = (label: string) => open[label] ?? (label === activeGroup);
  const toggle = (label: string) => setOpen((prev) => {
    const next = { ...prev, [label]: !isOpen(label) };
    try { localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  return (
    <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto">
      {groups.map((group) => {
        const expanded = isOpen(group.label);
        return (
          <div key={group.label}>
            <button
              onClick={() => toggle(group.label)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 hover:text-foreground rounded-md hover:bg-muted/60 transition-colors"
              aria-expanded={expanded}
            >
              <ChevronRight className={cn("h-3 w-3 shrink-0 transition-transform", expanded && "rotate-90")} />
              <span className="flex-1 text-left">{group.label}</span>
              {!expanded && group.visible.some((it) => isItemActive(it.to)) && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
            </button>
            {expanded && (
              <div className="space-y-0.5 mb-1 mt-0.5">
                {group.visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/app"}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                        isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted",
                      )
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function UserPanel({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  return (
    <div className="border-t border-border p-4 space-y-3">
      <div className="text-xs leading-tight">
        <div className="font-semibold text-foreground truncate">{user.full_name}</div>
        <div className="text-muted-foreground truncate">{user.email}</div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          <span className="inline-flex items-center rounded-md bg-accent/15 text-accent-foreground px-2 py-0.5 text-[10px] uppercase tracking-wider">
            {ROLE_LABEL[user.role] ?? user.role}
          </span>
          {user.company_name && (
            <span className="inline-flex items-center rounded-md bg-primary/10 text-primary-dark px-2 py-0.5 text-[10px] uppercase tracking-wider" title={`Vinculado a ${user.company_name} (${user.company_type})`}>
              {user.company_type === "gestora" ? "G·" : user.company_type === "originadora" ? "O·" : ""}{user.company_name.length > 16 ? user.company_name.slice(0, 16) + "…" : user.company_name}
            </span>
          )}
        </div>
      </div>
      <NavLink to="/app/profile" className={({ isActive }) => `text-xs text-center block py-1 rounded ${isActive ? "text-primary-dark font-semibold" : "text-primary-medium hover:underline"}`}>
        Meu perfil / alterar senha
      </NavLink>
      <Button variant="outline" size="sm" className="w-full" onClick={onLogout}>
        <LogOut className="h-4 w-4" /> Sair
      </Button>
    </div>
  );
}

const SIDEBAR_KEY = "vsc_sidebar_collapsed";

export function AppShell({ user, children }: { user: AuthUser; children: ReactNode }) {
  const navigate = useNavigate();
  const { setUser } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  // Produtos visíveis ao usuário (gating Agro × Consignado CLT). admin vê tudo.
  const isAdmin = user.role === "admin";
  const [products, setProducts] = useState<string[] | null>(null);
  const location = useLocation();
  useEffect(() => {
    apiFetch<{ products: string[] }>("/me/products").then((d) => setProducts(d.products)).catch(() => setProducts(["consignado_clt"]));
  }, []);
  // Guarda da área principal: rota de produto que o usuário não tem → volta ao dashboard.
  useEffect(() => {
    if (!products || isAdmin) return;
    const item = NAV_GROUPS.flatMap((g) => g.items).find((n) => n.product && (location.pathname === n.to || location.pathname.startsWith(n.to + "/")));
    if (item?.product && !products.includes(item.product)) navigate("/app", { replace: true });
  }, [products, isAdmin, location.pathname, navigate]);
  const toggleCollapsed = () => setCollapsed((c) => {
    const v = !c;
    try { localStorage.setItem(SIDEBAR_KEY, v ? "1" : "0"); } catch { /* ignore */ }
    return v;
  });

  async function handleLogout() {
    await logout();
    setUser(null);
    navigate("/login", { replace: true });
  }

  return (
    <div className="h-screen overflow-hidden flex bg-muted/40">
      {/* Sidebar desktop — retrátil (modelo sheet, retrai p/ esquerda) */}
      <aside
        className={cn(
          "hidden lg:flex shrink-0 flex-col border-border bg-background transition-[width] duration-200 ease-in-out overflow-hidden",
          "h-screen",
          collapsed ? "w-0 border-r-0" : "w-64 border-r",
        )}
      >
        <div className="h-16 flex items-center px-6 border-b border-border w-64">
          <Logo />
        </div>
        <NavList user={user} products={products} isAdmin={isAdmin} />
        <UserPanel user={user} onLogout={handleLogout} />
      </aside>

      {/* Drawer mobile */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] bg-background flex flex-col animate-fade-in">
            <div className="h-16 flex items-center justify-between px-6 border-b border-border">
              <Logo />
              <button onClick={() => setMobileOpen(false)} aria-label="Fechar menu" className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavList user={user} products={products} isAdmin={isAdmin} onNavigate={() => setMobileOpen(false)} />
            <UserPanel user={user} onLogout={handleLogout} />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 min-h-0 h-screen overflow-y-auto">
        {/* Header mobile com hamburger */}
        <header className="lg:hidden h-14 border-b border-border bg-background flex items-center px-4 justify-between sticky top-0 z-30">
          <button onClick={() => setMobileOpen(true)} aria-label="Abrir menu" className="p-2 -ml-2 rounded hover:bg-muted">
            <Menu className="h-5 w-5" />
          </button>
          <Logo />
          <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Sair">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        {/* Context bar global: toggle do sidebar + FIDC selector + Cmd+K hint */}
        <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 lg:top-0 z-20">
          <div className="px-4 lg:px-10 h-16 max-w-[1800px] mx-auto flex items-center justify-between gap-3">
            <button
              onClick={toggleCollapsed}
              className="hidden lg:inline-flex items-center justify-center h-8 w-8 -ml-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label={collapsed ? "Expandir menu" : "Retrair menu"}
              title={collapsed ? "Expandir menu" : "Retrair menu"}
            >
              {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
            <div className="flex items-center gap-3 ml-auto">
              <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground border border-border rounded px-2 py-0.5 bg-muted/30">
                <span>⌘</span>K
                <span className="ml-1 text-muted-foreground/70">saltar</span>
              </kbd>
              <FidcSelector />
            </div>
          </div>
        </div>
        <div className="px-4 lg:px-10 py-6 lg:py-8 max-w-[1800px] mx-auto animate-fade-in min-w-0">{children}</div>
      </main>
      <CommandPalette />
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="mb-6 lg:mb-8">
      {breadcrumb && <div className="mb-2">{breadcrumb}</div>}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <h1 className="text-2xl lg:text-3xl font-display font-bold text-primary-dark">{title}</h1>
          {subtitle && <p className="text-muted-foreground text-balance max-w-2xl text-sm lg:text-base">{subtitle}</p>}
        </div>
        {actions && <div className="flex gap-2 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}
